<template>
  <div :class="b()">
    <div :class="b('address-wrapper')">
      <e-picture v-if="showLocationImage && locationImage"
                 v-bind="locationImage"
                 :class="b('picture')"
                 :sizes="pictureSizes"
                 :ratio="2"
      />
      <h4 :class="b('title')">
        {{ $t('l-location-detail.address') }}
      </h4>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-html="location.address?.formattedAddress" :class="b('address')"></div>
    </div>
    <div :class="b('contact-wrapper')">
      <div v-if="showContactPerson && hasContactPerson" :class="b('contact-person')">
        <e-picture v-bind="contactPersonImage"
                   :class="b('picture')"
                   :sizes="pictureSizes"
                   :alt="contactPersonImage?.alt || ''"
                   :ratio="2"
        />
        <h4 :class="b('title')">
          {{ $t('l-location-detail.contactPerson') }}
        </h4>
        <div :class="b('subtitle', { name: true })">
          {{ contactPersonName }}
        </div>
        <div v-if="contactPersonEmail" :class="b('email-wrapper')">
          <a v-protect-mailto :href="`mailto:${contactPersonEmail}`" :class="b('email')">
            {{ emailLabel }}
          </a>
        </div>
      </div>
      <div v-if="hasPhoneNumbers" :class="b('phone-numbers')">
        <div v-if="location.address?.phone" :class="b('phone-number')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.phoneAbbreviation') }} {{ location.address?.phone }}
        </div>
        <div v-if="location.address?.fax" :class="b('phone-number')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.faxAbbreviation') }} {{ location.address?.fax }}
        </div>
        <div v-if="location.address?.cellphone" :class="b('phone-number')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.mobilePhoneAbbreviation') }} {{ location.address?.cellphone }}
        </div>
      </div>
    </div>
    <div :class="b('opening-hours-wrapper')">
      <div v-if="location.todayOpeningTime">
        <h5 :class="b('subtitle')">
          {{ $t('l-location-detail.todaysOpeningHoursTitle') }}
        </h5>
        {{ getFormattedOpeningTodayHours(location.todayOpeningTime) }}
      </div>
      <div v-if="openingWeekdaysHours?.length"
           :class="b('opening-weekdays')"
      >
        <h5 :class="b('subtitle')">
          {{ $t('l-location-detail.weekdaysOpeningHours') }}
        </h5>
        <dl :class="b('opening-weekday-list')">
          <div v-for="(day, index) in openingWeekdaysHours"
               :key="index"
               :class="b('opening-weekday-item')"
          >
            <dt :class="b('timeframe')">
              {{ day.timeframe }}
            </dt>
            <dd :class="b('opening-time')">
              {{ day.openingTime }}
            </dd>
          </div>
        </dl>
      </div>
      <div v-if="openingSpecialdaysHours?.length"
           :class="b('opening-specialdays')"
      >
        <h5 :class="b('subtitle')">
          {{ $t('l-location-detail.specialOpeningHours') }}
        </h5>
        <dl :class="b('opening-specialday-list')">
          <div v-for="(day, index) in openingSpecialdaysHours"
               :key="index"
               :class="b('opening-specialday-item')"
          >
            <dt :class="b('timeframe')">
              {{ day.timeframe }}
            </dt>
            <dd :class="b('opening-time')">
              {{ day.openingTime }}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';
  import getFormattedOpeningTodayHours from '@/helpers/opening-hours';
  import mapMediaContainerSrcSet, { MediaFormatFilter } from '@/helpers/map-media-container-srcset';
  import getFormattedOpeningWeekdaysHours, { FormattedOpeningWeekdaysHours } from '@/helpers/opening-days-hours';
  import { PointOfService } from '@/types/point-of-service';
  import { ImageSrcset } from '@/types/image';
  import { ImageSizes } from '@/types/sizes';
  import { HybrisType } from '@/setup/globals';
  import decodeBase64 from '@/helpers/decode-base64';

  type Setup = {
    pictureSizes: ImageSizes;
    getFormattedOpeningTodayHours: typeof getFormattedOpeningTodayHours;
  }

  // type Data = {}

  /**
   * Renders a detailed location tile.
   */
  export default defineComponent({
    name: 'c-location-tile-detailed',

    components: { ePicture, eIcon },

    props: {
      /**
       * Expects a location object.
       */
      location: {
        type: Object as PropType<PointOfService>,
        required: true,
      },

      /**
       * Defines whether the location image is displayed.
       */
      showLocationImage: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines whether the contact person is displayed.
       */
      showContactPerson: {
        type: Boolean,
        default: true,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        pictureSizes: {
          xxs: 439,
          xs: 727,
          sm: 951,
          md: 380,
          lg: 447,
          fallback: 447,
        },
        getFormattedOpeningTodayHours,
      };
    },
    // data(): Data {},

    computed: {
      locationImage(): ImageSrcset | undefined {
        const { medias } = this.location;

        if (Array.isArray(medias) && medias.length) {
          return medias && mapMediaContainerSrcSet({
            medias,
            description: '',
            type: HybrisType.MediaContainer,
          }, MediaFormatFilter.PointOfService);
        }

        return undefined;
      },

      contactPersonName(): string | undefined {
        const { firstName, lastName } = this.location.address || {};

        if (!firstName || !lastName) {
          return undefined;
        }

        return `${firstName} ${lastName}`;
      },

      contactPersonEmail(): string {
        const { email } = this.location.address || {};

        return email ? decodeBase64(email) : '';
      },

      /**
       * Based on the current data we don't have a good way to check if there is a contact person available.
       */
      hasContactPerson(): boolean {
        return !!this.contactPersonName;
      },

      hasPhoneNumbers(): boolean {
        const { phone, fax, cellphone } = this.location.address || {};

        return !!phone || !!fax || !!cellphone;
      },

      contactPersonImage(): ImageSrcset | undefined {
        const { contactImage } = this.location.address || {};

        return contactImage && mapMediaContainerSrcSet(contactImage);
      },

      emailLabel(): string | undefined {
        return import.meta.env.VITE_CONTACT_PERSON_TILE_EMAIL_ADDRESS_LABEL !== '1'
          ? this.$t('global.email')
          : this.contactPersonEmail;
      },

      openingWeekdaysHours(): FormattedOpeningWeekdaysHours[] | undefined {
        if (!this.location.openingHours?.weekDayOpeningList) {
          return undefined;
        }

        return getFormattedOpeningWeekdaysHours(this.location.openingHours?.weekDayOpeningList);
      },

      openingSpecialdaysHours(): FormattedOpeningWeekdaysHours[] | undefined {
        if (!this.location.openingHours?.specialDayOpeningList) {
          return undefined;
        }

        return getFormattedOpeningWeekdaysHours(this.location.openingHours?.specialDayOpeningList);
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-location-tile-detailed {
    display: flex;
    gap: variables.$spacing--40;
    flex-direction: column;

    @include mixins.media(md) {
      gap: variables.$spacing--20;
    }

    &__address-wrapper {
      padding-bottom: variables.$spacing--20;
      border-bottom: 4px solid variables.$color-grayscale--0;
    }

    &__contact-person {
      padding-bottom: variables.$spacing--20;

      .e-picture--placeholder:not(.e-picture--loaded) {
        border: 1px solid variables.$color-grayscale--400;
        background: url('../assets/placeholder-image-person.svg') no-repeat center;
      }
    }

    &__contact-wrapper {
      padding-bottom: variables.$spacing--20;
      border-bottom: 4px solid variables.$color-grayscale--0;

      &:empty {
        display: none;
      }
    }

    &__contact {
      display: flex;
      gap: variables.$spacing--5;
      flex-direction: column;
    }

    &__title {
      @include mixins.font(variables.$font-size--20, variables.$line-height--25, variables.$font-weight--bold);
    }

    &__subtitle {
      @include mixins.font(variables.$font-size--18, variables.$line-height--20, variables.$font-weight--bold);

      &--name {
        margin-bottom: variables.$spacing--15;
      }
    }

    &__email {
      @include mixins.link--primary();
      @include mixins.font(variables.$font-size--16, variables.$line-height--18, variables.$font-weight--bold);

      margin-bottom: variables.$spacing--10;

      @include mixins.media(md) {
        margin-bottom: variables.$spacing--25;
      }
    }

    &__picture {
      margin-bottom: variables.$spacing--25;
    }

    &__opening-hours-wrapper {
      display: flex;
      gap: variables.$spacing--30;
      flex-direction: column;
    }

    &__opening-weekdays,
    &__opening-specialdays {
      display: flex;
      gap: variables.$spacing--20;
      flex-direction: column;
    }

    &__opening-weekday-list,
    &__opening-specialday-list {
      display: flex;
      flex-wrap: wrap;
    }

    &__opening-weekday-item,
    &__opening-specialday-item {
      display: flex;
      width: 100%;
    }

    &__timeframe,
    &__opening-time {
      flex: 1;
    }

    &__phone-number {
      display: flex;
      gap: variables.$spacing--10;
      align-items: center;
    }
  }
</style>
